import React, { useMemo } from 'react';
import Modal from '..';
import CloseIcon from '@mui/icons-material/Close';
import { Stack } from '@mui/material';
import { ResponsiveContainer, LineChart as RechartsLineChart, Line as RechartsLine, XAxis, YAxis, Tooltip } from 'recharts';

// Define proper types for TypeScript
interface TrendData {
  isPositive: boolean;
  rateOfChange: number;
  firstValue: number;
  lastValue: number;
}

// interface HolderCountChartProps {
//   open: boolean;
//   onClose: () => void;
//   data: any[];
//   trendData?: TrendData;
// }

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // Use the timestamp from the data point
    const timestamp = payload[0]?.payload?.timestamp;
    const formattedDate = timestamp ? new Date(timestamp).toLocaleString() : label;
    const data = payload[0]?.payload;
    
    // Check if we have detailed data from Moralis
    const hasDetailedData = data && 
      (typeof data.netChange !== 'undefined' || 
       typeof data.percentChange !== 'undefined' ||
       typeof data.swapAcquisitions !== 'undefined');
    
    return (
      <div style={{ 
        backgroundColor: 'rgba(23, 23, 30, 0.95)', 
        borderColor: 'rgba(115, 246, 255, 0.2)',
        color: '#73F6FF',
        padding: '10px',
        borderRadius: '4px',
        fontSize: '12px',
        border: '1px solid rgba(115, 246, 255, 0.2)',
        minWidth: '200px'
      }}>
        <p style={{ color: '#73F6FF', fontWeight: 600, marginBottom: '5px' }}>
          {formattedDate}
        </p>
        
        {/* Always show holders count */}
        <p style={{ color: '#73F6FF', margin: '5px 0' }}>
          Holders: <strong>{data.holders.toLocaleString()}</strong>
        </p>
        
        {/* Show detailed information if available */}
        {hasDetailedData && (
          <>
            {typeof data.netChange !== 'undefined' && (
              <p style={{ 
                color: data.netChange >= 0 ? '#47ff9c' : '#ff4747',
                margin: '5px 0',
                fontWeight: 500
              }}>
                Net Change: {data.netChange > 0 ? '+' : ''}{data.netChange.toLocaleString()}
              </p>
            )}
            
            {typeof data.percentChange !== 'undefined' && (
              <p style={{ 
                color: data.percentChange >= 0 ? '#47ff9c' : '#ff4747',
                margin: '5px 0'
              }}>
                % Change: {data.percentChange > 0 ? '+' : ''}{(data.percentChange * 100).toFixed(2)}%
              </p>
            )}
            
            {(data.swapAcquisitions || data.transferAcquisitions || data.airdropAcquisitions) && (
              <div style={{ 
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid rgba(115, 246, 255, 0.2)'
              }}>
                <p style={{ color: 'rgba(115, 246, 255, 0.7)', fontSize: '11px', marginBottom: '5px' }}>
                  New Holders By:
                </p>
                {data.swapAcquisitions && (
                  <p style={{ fontSize: '11px', margin: '2px 0' }}>
                    Swap: {data.swapAcquisitions}
                  </p>
                )}
                {data.transferAcquisitions && (
                  <p style={{ fontSize: '11px', margin: '2px 0' }}>
                    Transfer: {data.transferAcquisitions}
                  </p>
                )}
                {data.airdropAcquisitions && (
                  <p style={{ fontSize: '11px', margin: '2px 0' }}>
                    Airdrop: {data.airdropAcquisitions}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
  return null;
};

// Custom Y-axis tick
const CustomYAxisTick = ({ x, y, payload }) => {
  // Format the value to a compact number with 1 decimal place
  const formattedValue = new Intl.NumberFormat('en-US', { 
    notation: 'compact', 
    maximumFractionDigits: 1 
  }).format(payload.value);
  
  return (
    <text 
      x={x} 
      y={y} 
      dy={4} 
      fill="#73F6FF40" 
      fontSize={12}
      textAnchor="end"
    >
      {formattedValue}
    </text>
  );
};

const HolderCountChart = ({ open, onClose, data, trendData }) => {
  // Prepare chart data once when inputs change
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Sort by timestamp ascending
    return [...data].sort((a, b) => {
      // Support both old and new data formats
      const dateA = new Date(a.timestamp || a.Date || '');
      const dateB = new Date(b.timestamp || b.Date || '');
      return dateA.getTime() - dateB.getTime();
    }).map(item => {
      // Create a standardized format for the chart
      const timestamp = item.timestamp || new Date(item.Date).getTime();
      const date = new Date(timestamp);
      
      return {
        // Short date for x-axis
        date: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
        // Full timestamp for tooltip
        timestamp: timestamp,
        // Value for the chart - support both data structures
        holders: item.Holder_Count || 0,
        // Include additional fields for enhanced tooltips if they exist
        netChange: item.netHolderChange,
        percentChange: item.holderPercentChange,
        // Extract acquisition methods if available
        swapAcquisitions: item.newHoldersByAcquisition?.swap,
        transferAcquisitions: item.newHoldersByAcquisition?.transfer,
        airdropAcquisitions: item.newHoldersByAcquisition?.airdrop
      };
    });
  }, [data]);
  
  // Calculate growth display for UI
  const getGrowthDisplay = () => {
    if (!trendData) return '';
    const { isPositive, rateOfChange } = trendData;
    const formattedRate = Math.abs(Number(rateOfChange)).toFixed(2);
    return `${isPositive ? '+' : '-'}${formattedRate}%`;
  };
  
  // Calculate average daily change
  const calculateAvgChange = () => {
    if (!chartData || chartData.length < 2) return 0;
    
    const validData = chartData.filter(item => typeof item.netChange !== 'undefined');
    if (validData.length === 0) return 0;
    
    const sum = validData.reduce((acc, item) => acc + (item.netChange || 0), 0);
    return sum / validData.length;
  };
  
  // Get the top acquisition source
  const getTopAcquisitionSource = () => {
    if (!chartData || chartData.length === 0) return 'N/A';
    
    let swapTotal = 0;
    let transferTotal = 0;
    let airdropTotal = 0;
    
    chartData.forEach(item => {
      swapTotal += item.swapAcquisitions || 0;
      transferTotal += item.transferAcquisitions || 0;
      airdropTotal += item.airdropAcquisitions || 0;
    });
    
    const max = Math.max(swapTotal, transferTotal, airdropTotal);
    
    if (max === 0) return 'N/A';
    if (max === swapTotal) return 'Swap';
    if (max === transferTotal) return 'Transfer';
    return 'Airdrop';
  };
  
  // Calculate volatility (standard deviation of percent changes)
  const calculateVolatility = () => {
    if (!chartData || chartData.length < 3) return 0;
    
    const validData = chartData.filter(item => typeof item.percentChange !== 'undefined');
    if (validData.length < 3) return 0;
    
    const values = validData.map(item => Math.abs(item.percentChange || 0) * 100);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length;
    return Math.sqrt(variance);
  };
  
  return (
    <Modal open={open} onClose={onClose}>
      <Stack
        direction={'row'}
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{ mb: 2 }}
      >
        <div style={{ 
          fontSize: '1.2rem', 
          fontWeight: 'bold',
          color: '#73F6FF'
        }}>
          Holder Count History
          {trendData && (
            <span style={{ 
              marginLeft: '10px',
              color: trendData.isPositive ? '#47ff9c' : '#ff4747',
              fontSize: '0.9rem'
            }}>
              {getGrowthDisplay()}
            </span>
          )}
        </div>
        <CloseIcon onClick={onClose} sx={{ cursor: 'pointer' }} />
      </Stack>
      
      {chartData.length === 0 ? (
        <div style={{ 
          height: 300, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'rgba(115, 246, 255, 0.5)'
        }}>
          No data available
        </div>
      ) : (
        <div style={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart
              data={chartData}
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <defs>
                <linearGradient id="colorHolders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#73F6FF" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#73F6FF" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="date" 
                stroke="#73F6FF40"
                fontSize={12}
              />
              <YAxis 
                stroke="#73F6FF40"
                tick={<CustomYAxisTick />}
                domain={['auto', 'auto']}
                scale="linear"
              />
              <Tooltip content={<CustomTooltip />} />
              <RechartsLine 
                type="monotone" 
                dataKey="holders" 
                name="Holders"
                stroke="#73F6FF" 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: '#73F6FF', strokeWidth: 2 }}
                fillOpacity={1}
                fill="url(#colorHolders)"
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {trendData && (
        <Stack
          direction={'column'}
          justifyContent="space-between"
          alignItems="stretch"
          width="100%"
          mt={2}
          spacing={2}
        >
          {/* Main metrics row */}
          <Stack
            direction={'row'}
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            sx={{
              backgroundColor: 'rgba(23, 23, 30, 0.5)',
              borderRadius: '8px',
              padding: '12px',
              border: '1px solid rgba(115, 246, 255, 0.1)'
            }}
          >
            <div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(115, 246, 255, 0.6)' }}>
                Start 
              </div>
              <div style={{ fontWeight: 'bold' }}>
                {trendData.firstValue.toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(115, 246, 255, 0.6)' }}>
                Current 
              </div>
              <div style={{ fontWeight: 'bold' }}>
                {trendData.lastValue.toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(115, 246, 255, 0.6)' }}>
                Change
              </div>
              <div style={{ 
                fontWeight: 'bold',
                color: trendData.isPositive ? '#47ff9c' : '#ff4747' 
              }}>
                {getGrowthDisplay()}
              </div>
            </div>
          </Stack>
          
          {/* Additional detailed information - only if we have it */}
          {chartData.length > 0 && chartData[0].netChange !== undefined && (
            <Stack
              direction={'row'}
              justifyContent="space-between"
              alignItems="flex-start"
              width="100%"
              sx={{
                backgroundColor: 'rgba(23, 23, 30, 0.5)',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid rgba(115, 246, 255, 0.1)'
              }}
            >
              {/* Average daily change */}
              <div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(115, 246, 255, 0.6)' }}>
                  Avg. Daily Change
                </div>
                <div style={{ 
                  fontWeight: 'bold',
                  color: calculateAvgChange() >= 0 ? '#47ff9c' : '#ff4747'
                }}>
                  {calculateAvgChange() > 0 ? '+' : ''}{calculateAvgChange().toFixed(1)}
                </div>
              </div>
              
              {/* Acquisition summary */}
              <div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(115, 246, 255, 0.6)' }}>
                  Top Acquisition 
                </div>
                <div style={{ fontWeight: 'bold' }}>
                  {getTopAcquisitionSource()}
                </div>
              </div>
              
              {/* Daily volatility */}
              <div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(115, 246, 255, 0.6)' }}>
                  Daily Volatility
                </div>
                <div style={{ fontWeight: 'bold' }}>
                  {calculateVolatility().toFixed(2)}%
                </div>
              </div>
            </Stack>
          )}
        </Stack>
      )}
    </Modal>
  );
};

export default HolderCountChart;